/**
 * Entidade de domínio que representa o texto de consentimento aprovado do
 * evento, exposto pelo backend via `GET /events/:id/config` (HUB-216).
 *
 * `null` sinaliza que nenhuma versão de consentimento foi aprovada ainda
 * para o evento — não é erro (HUB-201): quem consome deve exibir o texto de
 * fallback neutro (`FALLBACK_CONSENT_TEXT`), nunca bloquear a participação.
 */
export interface EventConsent {
  readonly version: number;
  readonly text: string;
}

export function createEventConsent(raw: unknown): EventConsent | null {
  if (raw === null || raw === undefined) return null;

  const data = raw as Record<string, unknown>;
  return {
    version: Number(data['version'] ?? 0),
    text: String(data['text'] ?? ''),
  };
}

/**
 * Texto neutro exibido quando o evento ainda não tem nenhuma versão de
 * consentimento aprovada. Não alega aprovação jurídica específica — apenas
 * informa a finalidade genérica do uso dos dados (LGPD, HUB-201).
 */
export const FALLBACK_CONSENT_TEXT =
  'Os dados informados serão usados apenas para os fins deste evento (contato e participação na ativação). Este evento ainda não possui um texto de consentimento específico cadastrado.';

/**
 * Resolve o texto de finalidade a ser exibido antes dos campos de captura
 * (HUB-201): o texto aprovado quando disponível, ou o fallback neutro.
 */
export function resolveConsentText(consent: EventConsent | null): string {
  return consent ? consent.text : FALLBACK_CONSENT_TEXT;
}

/**
 * Versão de consentimento usada quando nenhuma versão foi aprovada ainda
 * para o evento — mesmo comportamento hoje (piloto: só a versão 1 existe).
 */
export const FALLBACK_CONSENT_VERSION = 1;

/**
 * Resolve a versão de consentimento a ser enviada na captura do lead
 * (HUB-218): a versão realmente aprovada e exibida, ou o fallback quando
 * `consent` é `null`. Evita divergência entre o texto que o participante
 * leu e a versão registrada no lead.
 */
export function resolveConsentVersion(consent: EventConsent | null): number {
  return consent ? consent.version : FALLBACK_CONSENT_VERSION;
}
